import { publicEnv } from '@/lib/config/public-env';
/**
 * Which deployment is this, and which LogRocket project receives the session?
 *
 * One pure function, five environments, and every LogRocket decision a total
 * function over that union — so adding a sixth target is a compile error rather
 * than a silent misroute.
 *
 * Nothing here reads `ENVIRONMENT` or `ENV.isDevelopment`. That is the variable
 * that rotted (M4S-5593): `next.config.mjs` inlines `process.env.ENVIRONMENT ||
 * 'development'` at build time and CI never exports `ENVIRONMENT`, so deployed
 * builds very likely believe they are development. See docs/pdlc/observability.md.
 *
 * The impure readers live at the bottom; everything above them takes its inputs
 * as arguments so the tests can drive all five targets without touching
 * `process.env` or `window.__ENV`. The `NEXT_PUBLIC_*` values are read at
 * runtime through `publicEnv`, since one image serves every target.
 */

/** The five deployment targets Studio ships to. */
export type DeployEnvironment = 'local' | 'test1' | 'test2' | 'staging' | 'production';

/** Which signal decided the environment. Recorded so a fallback is visible. */
export type DeployEnvironmentSource = 'hostname' | 'nodeEnv' | 'appEnv' | 'fallback';

export interface DeployEnvironmentInputs {
    /** `window.location.hostname`, or undefined during SSR. */
    hostname?: string;
    /** `NEXT_PUBLIC_APP_ENV` — set per target by the deploy. */
    appEnv?: string;
    /** `NODE_ENV`. Only `next dev` sets this to 'development'; CI forces 'production'. */
    nodeEnv?: string;
}

export interface DeployEnvironmentResolution {
    environment: DeployEnvironment;
    source: DeployEnvironmentSource;
}

const DEPLOY_ENVIRONMENTS: readonly DeployEnvironment[] = [
    'local',
    'test1',
    'test2',
    'staging',
    'production',
];

/**
 * Hostnames whose leftmost label names a non-production target.
 *
 * Production is deliberately absent: no hostname maps *to* production, so an
 * unrecognised host can never be routed *away* from the production project. It
 * also means we never had to guess production's hostname to make this correct.
 */
const NON_PRODUCTION_HOST_LABELS: Record<string, DeployEnvironment> = {
    test1: 'test1',
    test2: 'test2',
    staging: 'staging',
};

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]', '0.0.0.0']);

export function isDeployEnvironment(value: unknown): value is DeployEnvironment {
    return typeof value === 'string' && DEPLOY_ENVIRONMENTS.includes(value as DeployEnvironment);
}

function normalizeHostname(hostname: string): string {
    return hostname.trim().toLowerCase().replace(/:\d+$/, '');
}

function isLoopback(host: string): boolean {
    return LOOPBACK_HOSTS.has(host) || host.endsWith('.localhost') || host.endsWith('.local');
}

/**
 * Resolve the deployment from the signals available in the browser.
 *
 * Order, and why:
 *  1. loopback hostname                → local
 *  2. leftmost host label test1/test2/staging → that target
 *  3. nodeEnv !== 'production'         → local
 *  4. a valid deployed `appEnv`        → that target
 *  5. fallback                         → production
 *
 * Hostname outranks configuration because it is ground truth in the browser at
 * the moment `LogRocket.init` runs: if a CI variable drifts, the host still
 * tells the truth. `appEnv` alone cannot be trusted for this, because
 * `.env.development` sets `NEXT_PUBLIC_APP_ENV='test1'` for local work — so
 * APP_ENV genuinely cannot tell local from test1.
 *
 * The fallback is `production` on purpose: an unrecognised host gets
 * production's privacy options (inputs sanitised, network redacted), never
 * local's relaxed ones. It has no say in which project a session is filed
 * under — see {@link resolveLogRocketRouting}.
 */
export function resolveDeployEnvironment(
    inputs: DeployEnvironmentInputs = {},
): DeployEnvironmentResolution {
    const { hostname, appEnv, nodeEnv } = inputs;

    if (hostname) {
        const host = normalizeHostname(hostname);
        if (isLoopback(host)) return { environment: 'local', source: 'hostname' };

        const label = host.split('.')[0];
        const mapped = NON_PRODUCTION_HOST_LABELS[label];
        if (mapped) return { environment: mapped, source: 'hostname' };
    }

    if (nodeEnv && nodeEnv !== 'production') return { environment: 'local', source: 'nodeEnv' };

    if (isDeployEnvironment(appEnv) && appEnv !== 'local') {
        return { environment: appEnv, source: 'appEnv' };
    }

    return { environment: 'production', source: 'fallback' };
}

// ---------------------------------------------------------------------------
// LogRocket project routing
// ---------------------------------------------------------------------------

export interface LogRocketRoutingInputs {
    /** `NEXT_PUBLIC_LOGROCKET_PROJECT` — the project this deployment records into. */
    configuredAppId?: string;
}

export type LogRocketRouting =
    /** A project was configured for this deployment and is being used. */
    | { appId: string; outcome: 'configured' }
    /** Nothing configured — LogRocket must not start. */
    | { appId: null; outcome: 'unconfigured' };

export type LogRocketRoutingOutcome = LogRocketRouting['outcome'];

/**
 * Pick the LogRocket project for a session, or decide there is none.
 *
 * Deliberately independent of {@link DeployEnvironment}. Each deployment names
 * its own project through one uniformly-named variable, rather than the code
 * deciding which environments are special — a rule of the form "production is
 * the one where you do not set it" is implicit knowledge, and eventually
 * somebody is not told.
 *
 * The environment still decides the `environment` trait and the privacy
 * options; it just has no say in where the session is filed.
 *
 * Absent or blank configuration means no project, and the caller must not
 * start LogRocket. There is deliberately no fallback: any default is somebody's
 * real project, and every developer machine without the variable would record
 * into it. The price is that a deployment which loses the variable stops
 * recording instead of misfiling, which is why it is set on every CircleCI
 * context, production included.
 */
export function resolveLogRocketRouting(inputs: LogRocketRoutingInputs = {}): LogRocketRouting {
    const trimmed = inputs.configuredAppId?.trim();

    if (!trimmed) {
        return { appId: null, outcome: 'unconfigured' };
    }

    return { appId: trimmed, outcome: 'configured' };
}

// ---------------------------------------------------------------------------
// SDK privacy options
// ---------------------------------------------------------------------------

export interface LogRocketPrivacyOptions {
    shouldCaptureIP: boolean;
    shouldDebugLog: boolean;
    sanitizeInputs: boolean;
    /** Redact auth headers and secret-shaped request bodies. */
    redactNetwork: boolean;
    baseHref: string | undefined;
}

/** The default apex domain, used when `NEXT_PUBLIC_COOKIE_DOMAIN` is absent. */
const DEFAULT_ROOT_HOSTNAME = 'mfourstudio.com';

/**
 * LogRocket's `rootHostname`, which stitches a session together across
 * subdomains. It must be the apex domain (`mfourstudio.com`), not the full host
 * (`staging.mfourstudio.com`) — the full host would break stitching.
 *
 * This used to come from `ENV.hostname`, which returns `'localhost'` whenever
 * `getEnvironment()` reads 'development' — i.e. in every deployed build if
 * `ENVIRONMENT` is unset. A `rootHostname` of `'localhost'` on a real domain is
 * not a subtle defect (M4S-5593).
 */
export function resolveRootHostname(environment: DeployEnvironment, cookieDomain?: string): string {
    if (environment === 'local') return 'localhost';
    const trimmed = cookieDomain?.trim().replace(/^\./, '');
    return trimmed || DEFAULT_ROOT_HOSTNAME;
}

/**
 * The SDK's privacy switches, as a function of the resolved environment.
 *
 * These used to hang off `ENV.isDevelopment`, which is `true` in deployed builds
 * whenever `ENVIRONMENT` is unset — leaving input sanitisation and network
 * redaction OFF in production. Deriving them here restores the intent, and is a
 * no-op if `ENVIRONMENT` turns out to have been set correctly all along.
 */
export function resolveLogRocketPrivacy(environment: DeployEnvironment): LogRocketPrivacyOptions {
    const isLocal = environment === 'local';

    return {
        shouldCaptureIP: !isLocal,
        shouldDebugLog: isLocal,
        // Everything that is not the developer's own machine carries real people's
        // data, so test1/test2/staging sanitise exactly as production does.
        sanitizeInputs: !isLocal,
        redactNetwork: !isLocal,
        baseHref: isLocal ? 'http://localhost:3000' : undefined,
    };
}

// ---------------------------------------------------------------------------
// Impure readers — the only place process.env / window are touched
// ---------------------------------------------------------------------------

/** Resolve the deployment from the live environment. Safe during SSR. */
export function getDeployEnvironment(): DeployEnvironmentResolution {
    return resolveDeployEnvironment({
        hostname: typeof window === 'undefined' ? undefined : window.location.hostname,
        appEnv: publicEnv('NEXT_PUBLIC_APP_ENV'),
        nodeEnv: process.env.NODE_ENV,
    });
}

/** Resolve LogRocket's cross-subdomain stitching root from the live environment. */
export function getLogRocketRootHostname(environment: DeployEnvironment): string {
    return resolveRootHostname(environment, publicEnv('NEXT_PUBLIC_COOKIE_DOMAIN'));
}

/** Resolve the LogRocket project from the live environment. */
export function getLogRocketRouting(): LogRocketRouting {
    return resolveLogRocketRouting({
        configuredAppId: publicEnv('NEXT_PUBLIC_LOGROCKET_PROJECT'),
    });
}
